import { Subscription } from 'rxjs';
import { ProcessParticipationService } from './../../../../services/process.participation.service';
import { ProcessParticipationAnswer } from './../../../../models/process-models';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { SurveyInfrastructure, Answer, QuestionType, CompositeAnswerPortion, MatrixCustomValue } from '../../../../models/questions';

@Component({
  selector: 'app-matrix-form',
  templateUrl: './matrix-form.component.html',
  styleUrls: ['../user-question-form-selector.component.scss']
})
export class MatrixFormComponent implements OnInit {
  @Input() question: SurveyInfrastructure;
  @Output() questionAnswered = new EventEmitter<SurveyInfrastructure>();
  answerRows: Array<Answer>;
  answerColumns: Array<Answer>;
  matrixForm: FormGroup;

  constructor(private fb: FormBuilder, private processParticipationservice: ProcessParticipationService) { }

  ngOnInit() {
    this.answerRows = this.getAnswerRows(this.question.answers);
    this.answerColumns = this.getAnswerColumns(this.question.answers);
    this.matrixForm = this.fb.group({
      columns: this.buildColumnAnswers(),
      rows: this.buildRowAnswers()
    });
  }

  buildColumnAnswers() {
    const answersColumnControl = this.getAnswerColumns(this.question.answers);
    return this.fb.array(answersColumnControl);
  }

  buildRowAnswers() {
    const answersRowControl = this.getAnswerRows(this.question.answers);
    return this.fb.array(answersRowControl);
  }

  getAnswerRowControls() {
    const answersControls = this.matrixForm.get('rows');
    return answersControls['controls'];
  }

  getAnswerColumnControls() {
    const answersControls = this.matrixForm.get('columns');
    return answersControls['controls'];
  }

  getColumnAnswer(index: number): Answer {
    return this.answerColumns[index];
  }

  getRowAnswer(index: number): Answer {
    return this.answerRows[index];
  }


  getUniqueInputName(rowIndex: number, columnIndex: number) {
    return this.question.id.toString() + this.getRowAnswer(rowIndex).id.toString() + this.getColumnAnswer(columnIndex).id.toString();
  }

  getIsMatrixCheckbox(): boolean {
    return this.question.answerType === QuestionType.MatrixCheckbox;
  }

  getMaxRowNameLength(answers: Array<Answer>): string {
    const rowNames: Array<string> = answers.map(x => x.name);
    return [...rowNames.sort((a, b) => {
      return b.length - a.length;
    })].shift();
  }

  isChecked(rowIndex: number, columnIndex: number) {
    const value: MatrixCustomValue = { row: this.getRowAnswer(rowIndex).id, column: this.getColumnAnswer(columnIndex).id };
    return this.question.processParticipationAnswers
      .some(x => this.parseCustomValue(x.customValue).column === value.column &&
        this.parseCustomValue(x.customValue).row === value.row);
  }

  selectAnswer(rowIndex: number, columnIndex: number) {
    const value: MatrixCustomValue = { row: this.getRowAnswer(rowIndex).id, column: this.getColumnAnswer(columnIndex).id };
    if (!this.getIsMatrixCheckbox()) {
      this.addOrUpdateRadiobuttonMatrix(value);
    } else {
      this.changeCheckboxMatrixValue(value);
    }
  }

  private addOrUpdateRadiobuttonMatrix(customValue: MatrixCustomValue) {
    const answerInColumn = this.question.processParticipationAnswers
      .find(x => this.parseCustomValue(x.customValue).row === customValue.row);
    const updatedAnswer = (answerInColumn) ? this.updateAnswer(answerInColumn, customValue) :
      this.crerateAnswer(customValue);
    this.processParticipationservice.addOrUpdateAnswer(this.question.processParticipationId, updatedAnswer)
    .subscribe(x => {
      this.question.processParticipationAnswers = x;
      this.questionAnswered.emit(this.question);
    });
  }

  private changeCheckboxMatrixValue(customValue: MatrixCustomValue) {
    const answer = this.question.processParticipationAnswers
      .find(x => x.customValue === JSON.stringify(customValue));
    if (answer) {
      this.deleteCheckboxeMatrixAnswer(answer);
      return;
    }
    this.processParticipationservice.addOrUpdateAnswer(this.question.processParticipationId, this.crerateAnswer(customValue))
      .subscribe(x => {
        this.question.processParticipationAnswers = x;
        this.questionAnswered.emit(this.question);
      });
  }

  private deleteCheckboxeMatrixAnswer(answer: ProcessParticipationAnswer) {
    this.processParticipationservice.deleteAnswer(this.question.processParticipationId, answer)
      .subscribe(x => {
        this.question.processParticipationAnswers = x;
        this.questionAnswered.emit(this.question);
      });
  }

  private parseCustomValue(customValue): MatrixCustomValue {
    return JSON.parse(customValue) as MatrixCustomValue;
  }

  private getAnswerRows(answers: Array<Answer>): Array<Answer> {
    return answers.filter(x => x.compositeAnswerPortion === CompositeAnswerPortion.Row);
  }

  private getAnswerColumns(answers: Array<Answer>): Array<Answer> {
    return answers.filter(x => x.compositeAnswerPortion === CompositeAnswerPortion.Column);
  }

  private crerateAnswer(customValue: MatrixCustomValue): ProcessParticipationAnswer {
    return {
      processParticipationId: this.question.processParticipationId,
      surveyInfrastructureId: this.question.id,
      customValue: JSON.stringify(customValue)
    };
  }

  private updateAnswer(answer: ProcessParticipationAnswer, customValue: MatrixCustomValue): ProcessParticipationAnswer {
    answer.customValue = JSON.stringify(customValue);
    return answer;
  }
}
